/**
 * #1220 — frontend interactive-activity signal.
 *
 * The admin "Last Active" column + WAU must reflect genuine human interaction,
 * not background traffic. This module records the timestamp of the most recent
 * real user gesture (pointer down, key press, form submit — and, transitively,
 * the route change a click triggers) so the axios request interceptor
 * (`api.ts` → `tagInteractiveRequest`) can tag ONLY user-initiated requests with
 * `X-User-Interaction: true`. The BFF forwards the marker and the backend bumps
 * the engagement signal only when it is present (default = background).
 *
 * Never tagged, by construction:
 * - the token-refresh timer / React-Query background refetch + polling — they
 *   fire with no recent gesture, so `wasRecentlyInteractive()` is false;
 * - SSE / EventSource streams — they never go through axios;
 * - health/ready probes — not issued by the app.
 *
 * The window is intentionally short: a request must follow a gesture closely to
 * count, so a background timer that happens to fire seconds after the last click
 * is not mistaken for interaction.
 */

/** A request counts as interactive only within this window after a gesture. */
export const INTERACTION_WINDOW_MS = 5000;

const GESTURE_EVENTS = ['pointerdown', 'keydown', 'submit'] as const;

let lastInteractionAt = 0;
let installed = false;

/** Record a genuine user interaction at `at` (defaults to now). */
export function markInteraction(at: number = Date.now()): void {
  lastInteractionAt = at;
}

/** True iff a genuine user gesture occurred within `INTERACTION_WINDOW_MS`. */
export function wasRecentlyInteractive(now: number = Date.now()): boolean {
  return lastInteractionAt > 0 && now - lastInteractionAt <= INTERACTION_WINDOW_MS;
}

/**
 * Install the global gesture listeners once (idempotent). Called at app
 * bootstrap (`index.tsx`). Capture-phase + passive so it observes every gesture
 * without interfering with handlers or scrolling.
 */
export function installInteractionListeners(target: EventTarget = window): void {
  if (installed) return;
  installed = true;
  for (const evt of GESTURE_EVENTS) {
    target.addEventListener(evt, () => markInteraction(), { capture: true, passive: true });
  }
}

/** Test-only: reset module state between cases. */
export function __resetInteractionSignalForTests(): void {
  lastInteractionAt = 0;
  installed = false;
}
